import RadioChecked from 'assets/icons/RadioChecked';
import RadioUnchecked from 'assets/icons/RadioUnchecked';
import Right from 'assets/icons/Right';
import Button from 'common/components/Button';
import ChipDropdown from 'common/components/ChipDropdown';
import LabelledInput from 'common/components/LabelledInput';
import LabelledPhoneInput from 'common/components/LabelledPhoneInput';
import { cn } from 'common/utils';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import CVModal from 'offers/details/components/modals/CVModal';
import React from 'react';
import { useTranslation } from 'react-i18next';

import EntrySection from './EntrySection';

const ApplicationTab = ({
  initialValues,
  validationSchema,
  handleSubmit,
  educationEntries,
  setEducationEntries,
  currentEducation,
  setCurrentEducation,
  experienceEntries,
  setExperienceEntries,
  currentExperience,
  setCurrentExperience,
  offer,
}) => {
  const { t } = useTranslation('applications\\index');

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid }) => (
        <Form
          className='space-y-6'
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        >
          {/* Personal Information Section */}
          <div className='border-t-2 pt-6'>
            <h2 className='text-lg uppercase font-semibold text-gray-600 mb-4'>
              {t('form.personalInformation')}
            </h2>
            <div className='grid grid-cols-1 md:grid-cols-2 md:gap-1'>
              <LabelledInput
                name='firstName'
                placeholder={t('form.fieldNames.firstName') + ' *'}
                type='text'
              />
              <LabelledInput
                name='lastName'
                placeholder={t('form.fieldNames.lastName') + ' *'}
                type='text'
              />
            </div>
            <LabelledInput
              name='location'
              placeholder={t('form.fieldNames.location') + ' *'}
              type='text'
            />
          </div>

          {/* Contact Section */}
          <div className='border-t-2 pt-6'>
            <h2 className='text-lg uppercase font-semibold text-gray-600 mb-4'>
              {t('form.contact')}
            </h2>
            <LabelledInput
              name='email'
              placeholder={t('form.fieldNames.email') + ' *'}
              type='email'
            />
            <LabelledPhoneInput
              name='phone'
              placeholder={t('form.fieldNames.phone') + ' *'}
              type='tel'
            />
          </div>

          {/* Curriculum Section */}
          <div className='border-t-2 pt-6 mb-8'>
            <h2 className='text-lg uppercase font-semibold text-gray-600 mb-4'>
              Curriculum
            </h2>
            <Field name='cvFile'>
              {({ field, form }) => (
                <CVModal
                  onFileUpload={(uploadedFile) => {
                    form.setFieldValue('cvFile', uploadedFile);
                    form.setFieldTouched('cvFile', true, false);
                  }}
                  hasError={form.touched.cvFile && form.errors.cvFile}
                  value={field.value}
                />
              )}
            </Field>
            <ErrorMessage
              name='cvFile'
              component='p'
              className='error text-bittersweet-shimmer text-xs font-semibold mt-1'
            />
          </div>

          {/* Education Section */}
          <EntrySection
            title={t('form.education')}
            entries={educationEntries}
            currentEntry={currentEducation}
            setCurrentEntry={setCurrentEducation}
            saveEntry={() => {
              setEducationEntries((prev) => [...prev, currentEducation]);
              setCurrentEducation(null);
            }}
            removeEntry={(index) =>
              setEducationEntries((prev) => prev.filter((_, i) => i !== index))
            }
            fields={[
              {
                name: 'degree',
                placeholder: 'form.fieldNames.degree',
                type: 'text',
              },
              {
                name: 'school',
                placeholder: 'form.fieldNames.school',
                type: 'text',
              },
              {
                name: 'field_of_study',
                placeholder: 'form.fieldNames.fieldOfStudy',
                type: 'text',
              },
              {
                name: 'start_date',
                placeholder: 'form.fieldNames.start_date',
                type: 'date',
              },
              {
                name: 'end_date',
                placeholder: 'form.fieldNames.end_date',
                type: 'date',
              },
            ]}
            addButtonText='form.addButton'
          />

          {/* Experience Section */}
          <EntrySection
            title={t('form.experience')}
            entries={experienceEntries}
            currentEntry={currentExperience}
            setCurrentEntry={setCurrentExperience}
            saveEntry={() => {
              setExperienceEntries((prev) => [...prev, currentExperience]);
              setCurrentExperience(null);
            }}
            removeEntry={(index) =>
              setExperienceEntries((prev) => prev.filter((_, i) => i !== index))
            }
            fields={[
              {
                name: 'title',
                placeholder: 'form.fieldNames.title',
                type: 'text',
              },
              {
                name: 'summary',
                placeholder: 'form.fieldNames.summary',
                type: 'text',
              },
              {
                name: 'company',
                placeholder: 'form.fieldNames.company',
                type: 'text',
              },
              {
                name: 'industry',
                placeholder: 'form.fieldNames.industry',
                type: 'text',
              },
              {
                name: 'start_date',
                placeholder: 'form.fieldNames.start_date',
                type: 'date',
              },
              {
                name: 'end_date',
                placeholder: 'form.fieldNames.end_date',
                type: 'date',
              },
            ]}
            addButtonText='form.addButton'
          />

          {/* Questions Section */}
          {offer?.questions?.length > 0 && (
            <div className='border-t pt-6'>
              <h2 className='text-lg uppercase font-semibold text-gray-600 mb-4'>
                {t('form.questions')}
              </h2>
              <div className='space-y-6'>
                {offer.questions.map((question) => (
                  <div key={question.slug} className='flex flex-col gap-2'>
                    <label
                      htmlFor={question.slug}
                      className='text-sm font-semibold text-gray-600'
                    >
                      {question.name + ' *'}
                    </label>
                    {question.description && (
                      <p className='text-sm text-gray-500'>
                        {question.description}
                      </p>
                    )}
                    {question.data_type === 'boolean' ? (
                      <div className='flex flex-col gap-2 mt-2'>
                        <Field name={`questions.${question.slug}`}>
                          {({ field, meta }) => (
                            <>
                              <label
                                className='flex items-center gap-2 cursor-pointer max-w-fit'
                                onClick={() =>
                                  field.onChange({
                                    target: { name: field.name, value: 'true' },
                                  })
                                }
                              >
                                <div className='flex-none w-4 h-4'>
                                  {field.value === 'true' ? (
                                    <RadioChecked size='16' color='#084D6E' />
                                  ) : meta.touched && meta.error ? (
                                    <RadioUnchecked size='16' color='#c2514b' />
                                  ) : (
                                    <RadioUnchecked size='16' color='#084D6E' />
                                  )}
                                </div>
                                <span
                                  className={cn(
                                    'text-sm text-dark-blue',
                                    meta.touched &&
                                      meta.error &&
                                      'text-bittersweet-shimmer',
                                  )}
                                >
                                  {t('form.yes')}
                                </span>
                              </label>

                              <label
                                className='flex items-center gap-2 cursor-pointer max-w-fit'
                                onClick={() =>
                                  field.onChange({
                                    target: {
                                      name: field.name,
                                      value: 'false',
                                    },
                                  })
                                }
                              >
                                <div className='flex-none w-4 h-4'>
                                  {field.value === 'false' ? (
                                    <RadioChecked size='16' color='#084D6E' />
                                  ) : meta.touched && meta.error ? (
                                    <RadioUnchecked size='16' color='#c2514b' />
                                  ) : (
                                    <RadioUnchecked size='16' color='#084D6E' />
                                  )}
                                </div>
                                <span
                                  className={cn(
                                    'text-sm text-dark-blue',
                                    meta.touched &&
                                      meta.error &&
                                      'text-bittersweet-shimmer',
                                  )}
                                >
                                  {t('form.no')}
                                </span>
                              </label>
                            </>
                          )}
                        </Field>
                        <ErrorMessage
                          name={`questions.${question.slug}`}
                          component='p'
                          className='error text-bittersweet-shimmer text-xs font-semibold'
                        />
                      </div>
                    ) : question.data_type === 'multiple_choice' ? (
                      <Field name={`questions.${question.slug}`}>
                        {({ field, form, meta }) => {
                          const isSingleAnswer = question.single_answer;
                          const selectedValues =
                            field.value || (isSingleAnswer ? '' : []);

                          const handleToggle = (option) => {
                            if (isSingleAnswer) {
                              form.setFieldValue(
                                `questions.${question.slug}`,
                                option,
                              );
                            } else {
                              const isSelected =
                                selectedValues.includes(option);
                              const updated = isSelected
                                ? selectedValues.filter((v) => v !== option)
                                : [...selectedValues, option];

                              form.setFieldValue(
                                `questions.${question.slug}`,
                                updated,
                              );
                            }
                          };

                          const hasError = meta.touched && meta.error;

                          return (
                            <div className='flex flex-col gap-2 mt-2'>
                              {question.choices?.map((option) => {
                                const isChecked = () => {
                                  if (isSingleAnswer) {
                                    return selectedValues === option;
                                  }
                                  return selectedValues.includes(option);
                                };

                                return (
                                  <label
                                    key={option}
                                    className='flex items-center gap-2 cursor-pointer max-w-fit'
                                    onClick={() => handleToggle(option)}
                                  >
                                    <div className='flex-none w-4 h-4'>
                                      {isChecked() ? (
                                        <RadioChecked
                                          size='16'
                                          color='#084D6E'
                                        />
                                      ) : hasError ? (
                                        <RadioUnchecked
                                          size='16'
                                          color='#c2514b'
                                        />
                                      ) : (
                                        <RadioUnchecked
                                          size='16'
                                          color='#084D6E'
                                        />
                                      )}
                                    </div>
                                    <span
                                      className={cn(
                                        'text-sm text-dark-blue',
                                        hasError && 'text-bittersweet-shimmer',
                                      )}
                                    >
                                      {option}
                                    </span>
                                  </label>
                                );
                              })}
                              <ErrorMessage
                                name={`questions.${question.slug}`}
                                component='p'
                                className='error text-bittersweet-shimmer text-xs font-semibold'
                              />
                            </div>
                          );
                        }}
                      </Field>
                    ) : question.data_type === 'dropdown' ? (
                      <Field name={`questions.${question.slug}`}>
                        {({ field, form, meta }) => {
                          const options =
                            question.choices?.map((choice) => ({
                              key: choice,
                              label: choice,
                            })) || [];

                          const currentOption = options.find(
                            (opt) => opt.key === field.value,
                          );

                          const hasError = meta.touched && meta.error;

                          return (
                            <div
                              className='flex flex-col gap-1 mt-2'
                              onClick={(e) => e.preventDefault()}
                            >
                              <ChipDropdown
                                placeholder={'form.dropdown-placeholder'}
                                options={options}
                                currentOption={currentOption}
                                chipVariant='input-outlined'
                                contextTranslationKey='applications\\index'
                                dropDownPosition='left'
                                hasError={hasError}
                                onChange={(value) =>
                                  form.setFieldValue(
                                    `questions.${question.slug}`,
                                    value,
                                  )
                                }
                              />
                              <ErrorMessage
                                name={`questions.${question.slug}`}
                                component='p'
                                className='error text-bittersweet-shimmer text-xs font-semibold'
                              />
                            </div>
                          );
                        }}
                      </Field>
                    ) : (
                      <LabelledInput
                        name={`questions.${question.slug}`}
                        placeholder={question.name}
                        type='text'
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className='sticky bottom-0 py-4 z-10 flex justify-end w-full'>
            <Button
              type='submit'
              variant='primary'
              className='px-6 py-2 opacity-100'
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? t('form.loading') : t('form.submitButton')}
              <Right size='16px' color='#FFFFFF' />
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ApplicationTab;
